package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.SaveDesignTemplateRequest
import dev.lingvo.shared.SavedDesignTemplateDto
import dev.lingvo.shared.SavedTemplateVisibility
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.applyScreenshotTemplate
import dev.lingvo.site.api.deleteScreenshotTemplate
import dev.lingvo.site.api.saveScreenshotTemplate
import dev.lingvo.site.api.savedScreenshotTemplates
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.dom.Div

/** Explicit saves use an acknowledged version; applications share the editor's optimistic head guard. */
@Composable
internal fun SavedTemplateLibrary(
    data: DesignPageData, controller: DesignAutosaveController, runner: ScreenshotsActionRunner,
    enabled: Boolean, gallery: Boolean, onBusy: (Boolean) -> Unit,
) {
    val subject = liveProjectRoute(data.project.slug)?.authoritySubject(Session.organizations)
    var name by remember(subject) { mutableStateOf("") }
    var visibility by remember(subject) { mutableStateOf(SavedTemplateVisibility.PROJECT) }
    var requestId by remember(subject, controller.version) { mutableStateOf<String?>(null) }
    var entries by remember(subject) { mutableStateOf<List<SavedDesignTemplateDto>>(emptyList()) }
    var reload by remember(subject) { mutableStateOf(0) }
    var busy by remember(subject) { mutableStateOf(false) }
    var failure by remember(subject) { mutableStateOf<String?>(null) }
    var notice by remember(subject) { mutableStateOf<String?>(null) }
    var apply by remember(subject) { mutableStateOf<SavedDesignTemplateDto?>(null) }
    var deleting by remember(subject) { mutableStateOf<SavedDesignTemplateDto?>(null) }
    val clean = controller.state == DesignSaveState.CLEAN
    val button = Modifier.styleModifier { property("min-height", "40px"); property("white-space", "normal"); property("max-width", "100%") }
    LaunchedEffect(subject, reload, gallery) {
        if (subject == null || !gallery) return@LaunchedEffect
        try {
            entries = requestProjectResource(subject.action, data.project.slug) {
                ApiClient.savedScreenshotTemplates(data.project.slug, data.plan.platform)
            }
            failure = null
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { failure = tr("app.projects.slug.screenshots.saved_template_library.couldnt_load_saved_templates_retry", "Couldn't load saved templates. Retry.") }
    }
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LSectionHeading(t("app.projects.slug.screenshots.saved_template_library.your_saved_templates", "Your saved templates"), description = t("app.projects.slug.screenshots.saved_template_library.reusable_layouts_with_slot_caption_placeholders", "Reusable layouts with slot-caption placeholders. Captures and translations stay with each project."))
        if (apply == null && deleting == null) failure?.let { LAlert(it, live = true) }
        Div(attrs = { attr("role", "status"); attr("aria-live", "polite"); attr("aria-atomic", "true") }) {
            notice?.let { LMuted(it) }
        }
        if (enabled || busy) {
            LTextField(name, { name = it.take(120); requestId = null }, label = t("app.projects.slug.screenshots.saved_template_library.template_name", "Template name"), enabled = enabled && !busy, modifier = Modifier.fillMaxWidth())
            LSelect(visibility.name, listOf(
                LSelectOption(SavedTemplateVisibility.PROJECT.name, t("app.projects.slug.screenshots.saved_template_library.private_to_this_project", "Private to this project")),
                LSelectOption(SavedTemplateVisibility.WORKSPACE.name, t("app.projects.slug.screenshots.saved_template_library.share_in_this_workspace", "Share in this workspace")),
            ), { visibility = SavedTemplateVisibility.valueOf(it); requestId = null }, label = t("app.projects.slug.screenshots.saved_template_library.template_visibility", "Template visibility"), enabled = enabled && !busy)
            if (!clean) LMuted(t("app.projects.slug.screenshots.saved_template_library.save_the_design_before_creating_or", "Save the design before creating or applying a template."))
            LButton(t("app.projects.slug.screenshots.saved_template_library.save_design_as_template", "Save design as template"), modifier = button, variant = LButtonVariant.SECONDARY,
                enabled = enabled && !busy && clean && controller.version > 0 && name.isNotBlank(), onClick = {
                    val id = requestId ?: (window.asDynamic().crypto.randomUUID() as String).also { requestId = it }
                    val request = SaveDesignTemplateRequest(id, controller.version, name.trim(), visibility)
                    failure = null; notice = null
                    runner.run(ScreenshotsPrivilege.DESIGN, { busy = it; onBusy(it) }, {
                        ApiClient.saveScreenshotTemplate(data.project.slug, data.plan.platform, request)
                    }, { failure = tr("app.projects.slug.screenshots.saved_template_library.couldnt_save_this_template_check_the", "Couldn't save this template. Check the saved version and artwork permissions, then retry.") }, {
                        requestId = null; name = ""; reload++
                        notice = tr("app.projects.slug.screenshots.saved_template_library.template_saved_browse_templates_to_reuse", "Template saved. Browse templates to reuse it.")
                    })
                })
        }
        if (gallery) {
            LButton(t("app.projects.slug.screenshots.saved_template_library.reload_saved_templates", "Reload saved templates"), onClick = { reload++ }, enabled = !busy, variant = LButtonVariant.GHOST)
            if (entries.isEmpty()) LMuted(t("app.projects.slug.screenshots.saved_template_library.no_saved_templates_are_available_for", "No saved templates are available for this platform."))
            entries.forEach { template ->
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.gap(Space.SM).styleModifier { property("overflow-wrap", "anywhere") }) {
                        LSectionHeading(template.name, description = if (template.visibility == SavedTemplateVisibility.WORKSPACE)
                            t("app.projects.slug.screenshots.saved_template_library.share_in_this_workspace", "Share in this workspace") else t("app.projects.slug.screenshots.saved_template_library.private_to_this_project", "Private to this project"))
                        LMuted(tf("screenshots.templates.provenance", "Saved by {person} · version {version}", "person" to template.createdBy, "version" to template.sourceVersion))
                        Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
                            LButton(t("app.projects.slug.screenshots.saved_template_library.use_this_saved_template", "Use this saved template"), modifier = button, variant = LButtonVariant.SECONDARY,
                                accessibleLabel = tf("screenshots.templates.use_named_template", "Use this saved template: {name}", "name" to template.name),
                                enabled = enabled && !busy && clean, onClick = { failure = null; notice = null; apply = template })
                            if (enabled) LButton(t("app.projects.slug.screenshots.saved_template_library.delete_saved_template", "Delete saved template"), modifier = button, variant = LButtonVariant.GHOST,
                                accessibleLabel = tf("screenshots.templates.delete_named_template", "Delete saved template: {name}", "name" to template.name),
                                enabled = !busy, onClick = { failure = null; notice = null; deleting = template })
                        }
                    }
                }
            }
        }
    }
    LConfirmDialog(apply != null, title = t("app.projects.slug.screenshots.saved_template_library.apply_saved_template", "Apply saved template?"),
        confirmLabel = t("app.projects.slug.screenshots.saved_template_library.use_this_saved_template", "Use this saved template"), tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.screenshots.saved_template_library.layout_replacement", "Layout replacement"),
        consequence = tf("screenshots.templates.apply_named_consequence", "Applies saved template {name} to this project's design layout. Captures and caption translations stay. The previous design remains in version history.",
            "name" to apply?.name.orEmpty()),
        busy = busy, error = failure, onCancel = { if (!busy) apply = null }, onConfirm = {
            val template = apply
            if (template != null && enabled && clean && !busy) {
                val base = controller.version
                val original = controller.document
                runner.run(ScreenshotsPrivilege.DESIGN, { busy = it; onBusy(it) }, {
                    ApiClient.applyScreenshotTemplate(data.project.slug, data.plan.platform, template.id, base)
                }, { failure = tr("app.projects.slug.screenshots.saved_template_library.couldnt_apply_this_template_reload_the", "Couldn't apply this template. Reload the design and template list before retrying.") }, { dto ->
                    if (controller.adoptRestored(dto, base, original)) {
                        apply = null; notice = tr("app.projects.slug.screenshots.saved_template_library.template_applied_open_design_to_edit", "Template applied. Open Design to edit its layers.")
                    } else failure = tr("app.projects.slug.screenshots.saved_template_library.the_design_changed_while_applying_reload", "The design changed while applying. Reload to see the saved version.")
                })
            }
        })
    LConfirmDialog(deleting != null, title = t("app.projects.slug.screenshots.saved_template_library.delete_saved_template_2", "Delete saved template?"),
        confirmLabel = t("app.projects.slug.screenshots.saved_template_library.delete_saved_template", "Delete saved template"), tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.screenshots.saved_template_library.template_library_removal", "Template library removal"),
        consequence = tf("screenshots.templates.delete_named_consequence", "Removes saved template {name}. Designs that already used it stay saved.",
            "name" to deleting?.name.orEmpty()),
        busy = busy, error = failure, onCancel = { if (!busy) deleting = null }, onConfirm = {
            val template = deleting
            if (template != null && enabled && !busy) runner.run(ScreenshotsPrivilege.DESIGN, { busy = it; onBusy(it) }, {
                ApiClient.deleteScreenshotTemplate(data.project.slug, data.plan.platform, template.id)
            }, { failure = tr("app.projects.slug.screenshots.saved_template_library.couldnt_delete_the_template_check_the", "Couldn't delete the template. Check the source project's permissions and retry.") }, {
                deleting = null; reload++
                notice = trf("screenshots.templates.deleted_named_template", "Saved template {name} was deleted.", "name" to template.name)
            })
        })
}
